import React from 'react';
import { Link } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StarRateIcon from '@mui/icons-material/StarRate';
import StorefrontIcon from '@mui/icons-material/Storefront';

import '../../css/home.css';

function MarketplaceProductCard({ product }) {
    if (!product) return null;

    const {
        _id,
        name = "Product",
        price = 0,
        discountActive = false,
        discountPrice = 0,
        /* No default. This used to be 4.5, so every product showed a rating
           whether or not anyone had reviewed it. */
        ratings,
        reviews,
        mainImage = [],
        image = [],
        storeName,
        storeSlug = "",
        distanceFormatted,
        productStock = 1,
        inStoreOffer,
        offlinePrice = null
    } = product;

    const finalPrice = discountActive ? Math.max(price - discountPrice, 0) : price;
    const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f4f4f5'/%3E%3Ccircle cx='200' cy='175' r='36' fill='%23e4e4e7'/%3E%3Cpath d='M155 240l30-38 22 26 33-42 35 54H155z' fill='%23d4d4d8'/%3E%3Ctext x='200' y='278' font-family='system-ui, sans-serif' font-size='13' font-weight='700' fill='%2371717a' text-anchor='middle'%3EIMSHOP%3C/text%3E%3C/svg%3E";
    const imageUrl = (mainImage && mainImage.length > 0 && mainImage[0].imageurl) ||
                     (image && image.length > 0 && image[0].imageurl) ||
                     placeholderImage;

    const reviewCount = Array.isArray(reviews) ? reviews.length : 0;
    const hasRating = Number(ratings) > 0 && reviewCount > 0;
    const inStock = productStock > 0;

    return (
        <div className="im-card im-product-card">
            <Link to={`/shoes/${_id}`} className="im-product__media">
                <img
                    src={imageUrl}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = placeholderImage;
                    }}
                />

                {distanceFormatted && (
                    <span className="im-badge im-badge--distance">
                        <LocationOnOutlinedIcon style={{ fontSize: '12px' }} />
                        {distanceFormatted}
                    </span>
                )}

                <span className={`im-stock ${inStock ? 'im-stock--in' : 'im-stock--out'}`}>
                    {inStock ? 'In stock nearby' : 'Out of stock'}
                </span>
            </Link>

            <div className="im-card__body">
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px'
                        }}
                    >
                        {/* Plain text rather than a link when there's no shop to link
                            to — this used to render an anchor pointing at "#". */}
                        {storeName && (
                            storeSlug ? (
                                <Link to={`/shop/${storeSlug}`} className="im-product__store">
                                    <StorefrontIcon style={{ fontSize: '13px' }} />
                                    <span>{storeName}</span>
                                </Link>
                            ) : (
                                <span className="im-product__store" style={{ color: 'var(--im-ink-500)' }}>
                                    <StorefrontIcon style={{ fontSize: '13px' }} />
                                    <span>{storeName}</span>
                                </span>
                            )
                        )}

                        {hasRating && (
                            <span className="im-rating">
                                <StarRateIcon style={{ fontSize: '13px' }} />
                                <span>{ratings}</span>
                                <span className="im-rating__count">({reviewCount})</span>
                            </span>
                        )}
                    </div>

                    <Link to={`/shoes/${_id}`} className="im-product__name">
                        {name}
                    </Link>
                </div>

                <div>
                    <div className="im-price">
                        <span className="im-price__now">
                            &#8377;{finalPrice.toLocaleString('en-IN')}
                        </span>
                        {discountActive && discountPrice > 0 && (
                            <span className="im-price__was">
                                &#8377;{price.toLocaleString('en-IN')}
                            </span>
                        )}
                    </div>

                    {/* Only shown when the shop actually published an in-store price
                        or offer. The old copy invented a prompt for every product. */}
                    {(offlinePrice || inStoreOffer) && (
                        <div className="im-instore">
                            {offlinePrice ? (
                                <span>
                                    In store: <strong>&#8377;{Number(offlinePrice).toLocaleString('en-IN')}</strong>
                                </span>
                            ) : (
                                <span>{inStoreOffer}</span>
                            )}
                        </div>
                    )}

                    {/* Subtle Fulfillment Capabilities */}
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', margin: '6px 0 10px', fontSize: '11px', color: '#6b7280', fontWeight: '700' }}>
                        <span style={{ background: '#f3f4f6', padding: '1.5px 6px', borderRadius: '4px' }}>🚚 Delivery</span>
                        <span style={{ background: '#f3f4f6', padding: '1.5px 6px', borderRadius: '4px' }}>🏪 Pickup</span>
                        <span style={{ background: '#f3f4f6', padding: '1.5px 6px', borderRadius: '4px' }}>📌 Reserve</span>
                    </div>

                    <Link
                        to={`/shoes/${_id}`}
                        className="im-btn im-btn--ghost"
                        style={{ width: '100%' }}
                    >
                        View Options &rarr;
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default MarketplaceProductCard;
